import { createDecorator } from "../../instantiation/common/registry.js"
import { IProperty, PropertyMetadata, ValueMetadata } from "./properties";
export type PropertyReadTarget = Pick<IProperty, "owner" | "key" | "metadata">;


// #region Service

export const IPropertyAccessorService = createDecorator<IPropertyAccessorService>('IPropertyAccessorService');

export interface GetAccessorOptions {

    property: IProperty,

    /** Value to write? */
    value?: unknown
}

/**
 * Service to provide read-write abilities for property values. This allows custom handling of complex property types (e.g., Three.js Vector3, Color, etc.)
 */
export interface IPropertyAccessorService {
    registerAccessor(accessor: IPropertyValueAccessor): void;

    readValue(property: PropertyReadTarget): unknown;

    canWrite(property: IProperty, newValue: unknown): boolean;
    /**
     * Write a value to a property.
     * @param property The property to write to
     * @param newValue The new value to set
     * @param metadata Optional metadata about the value change (source, filename, uri, etc.)
     * @param usageHint Optional hint for value conversion (e.g., "skybox" for HDRi textures)
     */
    writeValue(property: IProperty, newValue: unknown, metadata?: ValueMetadata, usageHint?: string): Promise<void>;

    cloneValue(property: IProperty): unknown;

    writeMetadata?: (object: unknown, metadata: ValueMetadata) => Promise<void>;
    readMetadata?: (object: unknown) => Promise<ValueMetadata | undefined>;
}

// export interface ValueReadOptions {
//     /**
//      * If true, the value will be converted to a human-readable format (if applicable). This should be used when exporting values for display purposes or to feeding into UI components.
//      */
//     humanReadable?: boolean;
// }


// #region Accessor

export interface Metadata {
    accept?: PropertyMetadata['accept'];
    static?: PropertyMetadata['static'];
}

export interface PropertyAccessContext {
    /**
     * If the property is being accessed as part of a larger property (e.g., accessing 'x' of a Vector3), this will point to the original object and key.
     * E.g. if we have a key 'position.x', the originalObject will be the object containing 'position' and the originalKey will be 'position'.
     */
    originalObject: object;
    /**
     * If the property is being accessed as part of a larger property (e.g., accessing 'x' of a Vector3), this will point to the original key.
     */
    originalKey: string;

    /**
     * Set to true if traversal to reach the property failed (e.g., intermediate property was undefined). In this case, the accessor should handle this gracefully (e.g., return undefined or a default value).
     */
    failedTraverse?: boolean;

    /**
     * Hint for value conversion, e.g., "skybox" for HDRi textures that need equirectangular mapping.
     * Passed through from setProperty command to guide how accessors/converters process the value.
     */
    usageHint?: "skybox" | string;
}

export interface IPropertyValueAccessor {

    canRead?(object: object, key: string, meta: Metadata): boolean;
    readValue?(object: object, key: string, context: PropertyAccessContext): any;


    // /**
    //  * Indicates whether the property is writeable (can be changed). If undefined, the default behavior is used.
    //  */
    // isWriteable?(object: object, key: string): boolean | undefined;

    canWrite?(object: Record<string, any>, key: string, newValue: unknown, meta: Metadata): boolean;
    writeValue?(object: Record<string, any>, key: string, newValue: unknown, context: PropertyAccessContext): Promise<void>;

    canClone?(object: Record<string, any>, key: string, value: unknown, meta: Metadata): boolean;
    cloneValue?(object: Record<string, any>, key: string, value: unknown): any;

    canWriteMetadata?(object: Record<string, any>, metadata: ValueMetadata): boolean;
    writeMetadata?(object: Record<string, any>, metadata: ValueMetadata): Promise<void>;

    canReadMetadata?(object: Record<string, any>): boolean;
    readMetadata?(object: Record<string, any>): Promise<ValueMetadata | undefined>;
}


export interface IPropertyValueAccessorWithReadAbility extends
    Required<Pick<IPropertyValueAccessor, 'canRead' | 'readValue'>> {
}

export function isPropertyValueAccessorWithReadAbility(accessor: IPropertyValueAccessor): accessor is IPropertyValueAccessorWithReadAbility {
    return typeof accessor.readValue === "function" && typeof accessor.canRead === "function";
}

export interface IPropertyValueAccessorWithWriteAbility extends
    Required<Pick<IPropertyValueAccessor, 'canWrite' | 'writeValue'>> {
}

export function isPropertyValueAccessorWithWriteAbility(accessor: IPropertyValueAccessor): accessor is IPropertyValueAccessorWithWriteAbility {
    return typeof accessor.writeValue === "function" && typeof accessor.canWrite === "function";
}

export interface IPropertyValueAccessorWithCloneAbility extends
    Required<Pick<IPropertyValueAccessor, 'cloneValue' | 'canClone'>> {
}

export function isPropertyValueAccessorWithCloneAbility(accessor: IPropertyValueAccessor): accessor is IPropertyValueAccessorWithCloneAbility {
    return typeof accessor.cloneValue === "function" && typeof accessor.canClone === "function";
}

export interface IPropertyValueAccessorWithMetadataWriteAbility extends
    Required<Pick<IPropertyValueAccessor, 'writeMetadata' | 'canWriteMetadata'>> {
}

export function isPropertyValueAccessorWithMetadataWriteAbility(accessor: IPropertyValueAccessor): accessor is IPropertyValueAccessorWithMetadataWriteAbility {
    return typeof accessor.writeMetadata === "function" && typeof accessor.canWriteMetadata === "function";
}

export interface IPropertyValueAccessorWithMetadataReadAbility extends
    Required<Pick<IPropertyValueAccessor, 'readMetadata' | 'canReadMetadata'>> {
}

export function isPropertyValueAccessorWithMetadataReadAbility(accessor: IPropertyValueAccessor): accessor is IPropertyValueAccessorWithMetadataReadAbility {
    return typeof accessor.readMetadata === "function" && typeof accessor.canReadMetadata === "function";
}
